/**
 * Diálogo de **consumo** de un rollo (galv por kg / acero por metros).
 *
 * Presentacional + hook de mutación: valida la cantidad con Zod
 * (`consumeSchema`, `safeParse` como el resto de formularios del proyecto),
 * muestra lo disponible y el resto estimado, y al confirmar llama a
 * `POST /inventario/consume` (vía `useConsumeRollo`). El error de la API se
 * muestra dentro del diálogo (no pantalla en blanco). Equivale al modal de consumo
 * del SSR (`inventario.html`), pero sobre la API REST.
 */
import { useEffect, useState } from "react";

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Input,
} from "@/components/ui";

import { inventarioErrorMessage } from "../format";
import { useConsumeRollo } from "../hooks/useInventarioMutations";
import { consumeSchema } from "../schemas";

/** Datos mínimos del rollo a consumir (galv o acero). */
export interface ConsumeTarget {
  id: number;
  /** Etiqueta legible (calibre/formato o perfil). */
  label: string;
  /** Unidad: "kg" (galv) o "m" (acero). */
  unidad: "kg" | "m";
  /** Cantidad disponible actual. */
  disponible: number;
}

interface ConsumeRolloDialogProps {
  target: ConsumeTarget | null;
  onClose: () => void;
}

export function ConsumeRolloDialog({ target, onClose }: ConsumeRolloDialogProps) {
  const consume = useConsumeRollo();

  const [cantidad, setCantidad] = useState("");
  const [fieldError, setFieldError] = useState<string | null>(null);

  // Limpia el formulario y el error de la API cada vez que se abre con otro rollo.
  useEffect(() => {
    if (target) {
      setCantidad("");
      setFieldError(null);
      consume.reset();
    }
    // Solo nos interesa reabrir con un target nuevo (la mutación es estable).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.id]);

  if (target === null) {
    return null;
  }

  const cantidadNum = Number(cantidad.replace(",", "."));
  const restante =
    Number.isFinite(cantidadNum) && cantidadNum > 0
      ? Math.max(0, target.disponible - cantidadNum)
      : target.disponible;

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (target === null) {
      return;
    }
    const result = consumeSchema.safeParse({ cantidad });
    if (!result.success) {
      setFieldError(result.error.flatten().fieldErrors.cantidad?.[0] ?? "Indica una cantidad");
      return;
    }
    setFieldError(null);
    consume.mutate(
      { rollo_id: target.id, cantidad: Number(result.data.cantidad.replace(",", ".")) },
      { onSuccess: () => onClose() },
    );
  }

  return (
    <Dialog open onOpenChange={(open) => (!open ? onClose() : undefined)}>
      <DialogContent>
        <DialogTitle>Consumir lote</DialogTitle>
        <DialogDescription>
          {target.label} · disponible {target.disponible.toFixed(1)} {target.unidad}
        </DialogDescription>

        <form className="mt-4 flex flex-col gap-4" onSubmit={handleSubmit} noValidate>
          <div className="flex flex-col gap-1">
            <label htmlFor="consume-cantidad" className="text-sm font-medium text-slate-700">
              Cantidad a consumir ({target.unidad})
            </label>
            <Input
              id="consume-cantidad"
              type="text"
              inputMode="decimal"
              autoComplete="off"
              autoFocus
              value={cantidad}
              aria-invalid={fieldError ? true : undefined}
              aria-describedby={fieldError ? "consume-cantidad-error" : undefined}
              onChange={(event) => setCantidad(event.target.value)}
            />
            {fieldError ? (
              <p id="consume-cantidad-error" className="text-sm text-red-600" role="alert">
                {fieldError}
              </p>
            ) : null}
          </div>

          <p className="text-sm text-slate-500">
            Resto estimado:{" "}
            <strong className="text-slate-800">
              {restante.toFixed(1)} {target.unidad}
            </strong>
            {restante <= 0 ? " (el lote se eliminará)" : ""}
          </p>

          {consume.isError ? (
            <p className="text-sm text-red-600" role="alert">
              {inventarioErrorMessage(consume.error)}
            </p>
          ) : null}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={consume.isPending}>
              {consume.isPending ? "Consumiendo…" : "Consumir"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
